import type { ReactNode } from "react";
import { Code2, Globe, Layers, Repeat, Settings2 } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { StepCodeBlock } from "../StepCodeBlock";
import { getString, getNumber } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getHttpRequestSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const url = getString(config.url);
  const method = getString(config.method) || "POST";
  const bodyTemplate = getString(config.bodyTemplate);
  const dataPath = getString(config.dataPath);
  const outputMode = getString(config.outputMode) || "replace";
  const outputField = getString(config.outputField);
  const concurrency = getNumber(config.concurrency) || 1;

  if (url) {
    items.push(
      <div key="endpoint" className="flex items-start gap-2 text-xs">
        <span className="mt-0.5 shrink-0 text-gray-400"><Globe className="size-3.5" /></span>
        <span className="inline-flex min-w-0 items-baseline gap-0 overflow-hidden rounded border border-rose-200 dark:border-rose-800">
          <span className="shrink-0 bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
            {method}
          </span>
          <span className="break-all px-1.5 py-0.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">
            {url}
          </span>
        </span>
      </div>
    );
  }

  if (bodyTemplate) {
    let prettyBody = bodyTemplate;
    try {
      prettyBody = JSON.stringify(JSON.parse(bodyTemplate), null, 2);
    } catch {
      // keep raw
    }
    items.push(
      <StepCodeBlock key="bodyTemplate" icon={<Code2 className="size-3.5" />} label={t("summaries.http_request.label_body_template")} code={prettyBody} title="Body Template" language="json" />
    );
  }

  if (dataPath) {
    items.push(
      <StepInfoRow key="dataPath" icon={<Layers className="size-3.5" />} label={t("summaries.http_request.label_data_path")} value={dataPath} mono />
    );
  }

  const outputModeLabels: Record<string, string> = {
    replace: t("summaries.http_request.output_replace"),
    merge: t("summaries.http_request.output_merge"),
    field: t("summaries.http_request.output_field", { field: outputField || "response" }),
  };
  items.push(
    <StepInfoRow key="outputMode" icon={<Settings2 className="size-3.5" />} label={t("summaries.http_request.label_output")} value={outputModeLabels[outputMode] || outputMode} />
  );

  if (concurrency > 1) {
    items.push(
      <StepInfoRow key="concurrency" icon={<Repeat className="size-3.5" />} label={t("summaries.http_request.label_concurrency")} value={t("summaries.http_request.concurrency_workers", { count: concurrency })} />
    );
  }

  return items;
}
